import { ConfigProvider } from 'antd';
import plPL from 'antd/locale/pl_PL';
import dayjs from 'dayjs';
import 'dayjs/locale/pl';
import { FONT_STACK, TOKENS } from '@/styles/theme.constants';

// Tyle, ile obsługujemy dziś. Drugi język to dopisanie wpisu tutaj (locale AntD + dayjs)
// i katalogu w `@/i18n/locales`, a wybór idzie po `i18n.language`.
dayjs.locale('pl');

// Hoisted to module scope so the same reference is passed to
// ConfigProvider on every render. The previous inline-object form
// created a fresh theme object each call, and Antd's ConfigProvider
// compares by reference — a new reference triggers an internal
// theme-token re-application that cascades CSS-var updates through
// every descendant Antd component. Stable reference = no cascade.
export const ANT_THEME = {
  cssVar: true,
  token: {
    colorPrimary: TOKENS.brand,
    colorTextBase: TOKENS.ink,
    colorBgBase: TOKENS.card,
    colorSuccess: TOKENS.success,
    // Pinned, not derived. Antd's light steps add 0.05 per step to HSV value, and
    // this green sits at V=0.54 where the status siblings sit near 0.73, so the
    // derived tint stops at a mid grey-green instead of a near-white one. That is
    // the Tag background, and it also disagreed with the SCSS chip tint.
    colorSuccessBg: TOKENS.successSoft,
    colorWarning: TOKENS.warning,
    colorError: TOKENS.danger,
    colorInfo: TOKENS.info,
    colorLink: TOKENS.brand,
    fontFamily: FONT_STACK,
    fontSize: 14,
    borderRadius: TOKENS.radiusControl,

    // Antd derives neutrals from the seeds: colorText is alpha 0.88 of colorTextBase,
    // colorTextSecondary 0.65, and colorBorder is colorBgBase darkened 15 steps.
    // Seeds alone never land on the mockup values, so these aliases are pinned.
    colorText: TOKENS.ink,
    colorTextSecondary: TOKENS.muted,
    // colorBorder is the control outline (input, select, checkbox), so it gets the
    // brand-tinted border. colorBorderSecondary is the divider and stays the flat
    // line. Pinning both to line made a bare checkbox invisible at 1.17:1.
    colorBorder: TOKENS.brandBorder,
    colorBorderSecondary: TOKENS.line,
    colorBgContainer: TOKENS.card,
    colorPrimaryHover: TOKENS.brandStrong,
    colorPrimaryActive: TOKENS.brandDeep,
    colorPrimaryBg: TOKENS.brandSoft,
    colorPrimaryBorder: TOKENS.brandBorder,
    borderRadiusLG: TOKENS.radiusCard,
  },
  components: {
    Card: { paddingLG: 0 },
    // The page background comes from SCSS, so Layout must not paint its own.
    // A token-level colorBgLayout would lose to this anyway: antd merges the
    // component override before deriving bodyBg from it.
    Layout: { colorBgLayout: 'none' },
  },
} as const;

export function AntConfigProvider({ children }: { children?: React.ReactNode }) {
  return (
    <ConfigProvider locale={plPL} theme={ANT_THEME}>
      {children}
    </ConfigProvider>
  );
}
